import React, { useEffect } from "react";
import Auth from "./components/Auth";
import {
  NotificationProvider,
  NotificationToaster,
} from "./contexts/NotificationContext";
import { AppRouter } from "./components/AppRouter";
import { useAuth } from "./contexts/AuthContext";
import VerificationPage from "./components/VerificationPage";
import MediaVerifyPage from "./components/MediaVerifyPage";
import PublicCalendarPage from "./components/PublicCalendarPage";
import ResetPassword from "./components/ResetPassword";
import SupportPage from "./components/SupportPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import JobLifecycleToasts from "./components/JobLifecycleToasts";
import { useGlobalRealtime } from "./hooks/useGlobalRealtime";
import {
  DesignSystemProvider,
  defaultDesignTokens,
} from "./lib/design/designSystem";
import FullScreenLoader from "./components/ui/FullScreenLoader";
import i18n from "./lib/i18n";
import { useLanguageStore } from "./state/useLanguageStore";

const App: React.FC = () => {
  useGlobalRealtime();
  const { session, isLoading } = useAuth();
  const language = useLanguageStore((state) => state.language);

  useEffect(() => {
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
    i18n.changeLanguage(language);
  }, [language]);

  const appShellClass = `${defaultDesignTokens.layout.appShell} ${defaultDesignTokens.surfaces.app} prevent-overflow`;
  const canvasShellClass = `${defaultDesignTokens.layout.fullHeight} ${defaultDesignTokens.surfaces.canvas}`;

  // Check for parameters (token) to distinguish between verification types
  const hasToken = React.useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.includes("?")
        ? window.location.hash.split("?")[1]
        : ""
    );
    return searchParams.has("token") || hashParams.has("token");
  }, []);

  const isVerificationPage = React.useMemo(() => {
    // Mission Order Verification: /verify/ID (and NO token)
    const isVerifyPath = 
      window.location.hash.startsWith("#/verify/") ||
      window.location.pathname.startsWith("/verify/");
    
    return isVerifyPath && !hasToken;
  }, [hasToken]);

  const isResetPasswordPage = React.useMemo(() => {
    // Check if user is on reset password page
    const isOnResetPasswordRoute =
      window.location.hash.startsWith("#/reset-password") ||
      window.location.pathname === "/reset-password";

    // Also check if URL contains recovery tokens (fallback for incorrect redirects)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    const type = hashParams.get("type") || searchParams.get("type");
    const hasRecoveryToken = type === "recovery";

    return isOnResetPasswordRoute || hasRecoveryToken;
  }, []);

  const isSupportPage = React.useMemo(() => {
    return (
      window.location.hash === "#/support" ||
      window.location.pathname === "/support"
    );
  }, []);

  const isPrivacyPage = React.useMemo(() => {
    return (
      window.location.hash === "#/privacy" ||
      window.location.pathname === "/privacy"
    );
  }, []);

  // Check for media verification page (public QR scan result)
  const isMediaVerifyPage = React.useMemo(() => {
    // Explicit route check
    const isExplicitMediaRoute = 
      window.location.pathname === "/media-verify" ||
      window.location.hash.startsWith("#/media-verify");
    
    // Generic route check with token (/verify?token=...)
    // Allow trailing slash for robustness
    const path = window.location.pathname;
    const hash = window.location.hash;
    const isVerifyRoute = 
      path === "/verify" || path === "/verify/" || 
      hash === "#/verify" || hash === "#/verify/";

    return isExplicitMediaRoute || (isVerifyRoute && hasToken);
  }, [hasToken]);

  const isPublicCalendarPage = React.useMemo(() => {
    const path = window.location.pathname;
    return /^\/c\/([0-9a-f-]{36}|team\/[0-9a-f-]{36})\/?$/i.test(path);
  }, []);

  // Priority: Check specific public pages first
  
  // Render media verification page (public QR code verification)
  // Check this BEFORE standard verification to catch /verify?token cases
  if (isMediaVerifyPage) {
    return (
      <DesignSystemProvider>
        <MediaVerifyPage />
      </DesignSystemProvider>
    );
  }

  if (isPublicCalendarPage) {
    return (
      <DesignSystemProvider>
        <PublicCalendarPage />
      </DesignSystemProvider>
    );
  }

  if (isVerificationPage) {
    return (
      <DesignSystemProvider>
        <div className={appShellClass}>
          <div className={canvasShellClass}>
            <VerificationPage />
          </div>
        </div>
      </DesignSystemProvider>
    );
  }

  if (isPrivacyPage) {
    return (
      <DesignSystemProvider>
        <div className={appShellClass}>
          <div className={canvasShellClass}>
            <PrivacyPolicyPage />
          </div>
        </div>
      </DesignSystemProvider>
    );
  }

  if (isSupportPage) {
    return (
      <DesignSystemProvider>
        <div className={appShellClass}>
          <div className={canvasShellClass}>
            <SupportPage />
          </div>
        </div>
      </DesignSystemProvider>
    );
  }

  if (isLoading) {
    return (
      <DesignSystemProvider>
        <FullScreenLoader />
      </DesignSystemProvider>
    );
  }

  return (
    <DesignSystemProvider>
      <NotificationProvider>
        <div className={appShellClass}>
          <NotificationToaster />
          {session ? <JobLifecycleToasts /> : null}
          <div className={canvasShellClass}>
            {!session ? <Auth /> : <AppRouter />}
          </div>
        </div>
      </NotificationProvider>
    </DesignSystemProvider>
  );
};

export default App;
